import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { supabase } from '../../lib/supabase';
import type { Service } from '../../lib/supabase';

interface PageContent {
  [key: string]: string;
}

export default function Services() {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [content, setContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState<any>(null);

  useEffect(() => {
    loadData();
    loadSiteSettings();
  }, []);

  const loadSiteSettings = async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('*')
        .single();
      
      if (data) {
        setSiteSettings(data);
      }
    } catch (error) {
      console.error('Error loading site settings:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [servicesRes, contentRes] = await Promise.all([
        supabase.from('services').select('*').eq('is_active', true).order('display_order'),
        supabase.from('page_content').select('*').eq('page_name', 'services')
      ]);

      setServices(servicesRes.data || []);

      const contentMap: PageContent = {};
      contentRes.data?.forEach((item) => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-[#FCD34D] animate-spin"></i>
      </div>
    );
  }

  // Arka plan stili
  const backgroundStyle = siteSettings?.background_image_enabled && siteSettings?.background_image_url
    ? {
        backgroundImage: `url(${siteSettings.background_image_url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }
    : { backgroundColor: '#0A0A0A' };

  return (
    <div className="min-h-screen" style={backgroundStyle}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
            Hizmetlerimiz
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            Aracınız için profesyonel bakım ve yenileme çözümleri
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <i className="ri-loader-4-line text-4xl text-[#FCD34D] animate-spin"></i>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  onClick={() => window.REACT_APP_NAVIGATE(`/services/${service.slug}`)}
                  className="bg-white/10 backdrop-blur-md rounded-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group border border-white/20"
                >
                  <div className="relative h-64 w-full overflow-hidden">
                    <img
                      src={service.image_url}
                      alt={service.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                      {service.name}
                    </h3>
                    <p className="text-white mb-4 line-clamp-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                      {service.description}
                    </p>
                    
                    <div className="flex items-center justify-end">
                      <button className="px-6 py-2 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#FCD34D]/90 transition-all whitespace-nowrap shadow-lg">
                        Detaylar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">
            {content.cta_title || t('services.cta.title')}
          </h2>
          <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto drop-shadow-md">
            {content.cta_subtitle || t('services.cta.subtitle')}
          </p>
          <Link 
            to="/appointment" 
            className="whitespace-nowrap inline-block px-10 py-5 bg-[#FCD34D] text-black font-bold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer text-lg"
          >
            {t('services.cta.button')}
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
